import type {
  ActionArgs,
  ActionData,
  AdminAction,
  AdminError,
} from '@contract';
import { adminApiEndpoint, publishableKey, supabase } from './supabase';

/** Where one cell's admin-api is, and whose session speaks to it. */
export interface AdminTarget {
  client: { auth: Pick<typeof supabase.auth, 'getSession'> };
  endpoint: string;
  key: string;
}

/** An admin-api refusal, with its contract code. */
export class AdminApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(error: AdminError, status: number) {
    super(error.message);
    this.name = 'AdminApiError';
    this.code = error.code;
    this.status = status;
  }
}

/** Codes after which the session is over and the portal signs out. */
const SIGN_OUT_CODES = new Set([
  'UNAUTHORIZED',
  'SESSION_ENDED',
  'SESSION_EXPIRED',
  'NOT_STAFF',
  'STAFF_DISABLED',
]);

/** Whether this refusal means the session it was sent with is over. */
export function isSessionOver(error: unknown): boolean {
  return error instanceof AdminApiError && SIGN_OUT_CODES.has(error.code);
}

type Listener = (error: AdminApiError) => void;
const sessionListeners = new Set<Listener>();

/** Called when admin-api says the session is over. */
export function onSessionRefused(listener: Listener): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

/** Calls one admin-api action with the staff session. The browser's fetch. */
export function callAdmin<A extends AdminAction>(
  action: A,
  args: ActionArgs<A>,
): Promise<ActionData<A>> {
  return callAdminWith(
    { client: supabase, endpoint: adminApiEndpoint, key: publishableKey },
    action,
    args,
    true,
  );
}

/**
 * The same call against any cell's admin-api. `endsSession` is true only for
 * the cell this tab is signed in to: a refusal from another cell must not
 * sign the person out of this one.
 */
export async function callAdminWith<A extends AdminAction>(
  target: AdminTarget,
  action: A,
  args: ActionArgs<A>,
  endsSession = false,
): Promise<ActionData<A>> {
  const { data } = await target.client.auth.getSession();
  const token = data.session?.access_token;

  const headers: Record<string, string> = {
    'content-type': 'application/json',
    apikey: target.key,
  };
  if (token) headers.authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(target.endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ action, args }),
    });
  } catch {
    throw new AdminApiError(
      {
        code: 'NETWORK',
        message: 'Could not reach Sao. Check the connection.',
      },
      0,
    );
  }

  let body: { data?: ActionData<A>; error?: AdminError } = {};
  try {
    body = await response.json();
  } catch {
    // Handled below.
  }

  if (!response.ok || body.error) {
    const error = new AdminApiError(
      body.error ?? {
        code: 'INTERNAL',
        message: 'Something went wrong. Try again.',
      },
      response.status,
    );
    if (endsSession && SIGN_OUT_CODES.has(error.code)) {
      for (const listener of sessionListeners) listener(error);
    }
    throw error;
  }
  return body.data as ActionData<A>;
}
